// Milestone derivation for the run hand-off (UX audit C, Option A).
// TRANSITION-based, never a fake percent: each milestone flips on real
// chain events (the server parses chain output into typed SSE events;
// the stream replays from event 0 on resume, so a reloaded tab can
// reconstruct the full list). Batched fan-out has long quiet stretches —
// that's why the current milestone carries a caption instead of progress.
import type { SseEvent } from "./api";

export type MilestoneState = "done" | "now" | "todo";

export interface Milestone {
  key: string;
  label: string;
  state: MilestoneState;
  /** Shown only while the milestone is current. */
  caption?: string;
}

function stageEvent(events: SseEvent[], type: string, stage: string): boolean {
  return events.some((e) => {
    if (e.type !== type) return false;
    const d = e.data as { stage?: string } | null;
    return !!d && d.stage === stage;
  });
}

/**
 * The five user-facing milestones, derived from the current beat + the
 * accumulated SSE events of THIS phase (preview events during the preview
 * beat; convert events after promotion — the convert beat implies the
 * preview work is done).
 */
export function deriveMilestones(args: {
  convertRunning: boolean;
  events: SseEvent[];
}): Milestone[] {
  const { convertRunning, events } = args;

  const discoveryDone =
    convertRunning || stageEvent(events, "stage_done", "discovery");
  const fanoutDone = convertRunning && stageEvent(events, "stage_done", "fanout");
  const auditRunning =
    convertRunning && stageEvent(events, "progress", "audit");
  const auditDone = convertRunning && stageEvent(events, "stage_done", "audit");

  // States cascade: a later milestone being live marks all earlier done.
  const grounding: MilestoneState = !convertRunning
    ? "todo"
    : auditRunning || auditDone || fanoutDone
      ? "done"
      : "now";
  const checking: MilestoneState = !convertRunning
    ? "todo"
    : auditDone
      ? "done"
      : auditRunning
        ? "now"
        : "todo";
  const locking: MilestoneState = auditDone ? "now" : "todo";

  return [
    { key: "received", label: "Messages received", state: "done" },
    {
      key: "firstread",
      label: "First read-through — patterns spotted",
      state: discoveryDone ? "done" : "now",
    },
    {
      key: "grounding",
      label: "Grounding each pattern in exact quotes",
      state: grounding,
      caption: grounding === "now" ? "usually the longest step" : undefined,
    },
    {
      key: "checking",
      label: "Checking every quote against your record",
      state: checking,
    },
    {
      key: "locking",
      label: "Locking your file with your passphrase",
      state: locking,
    },
  ];
}
